import React, { useState } from 'react';
// Types
import { FormInstance } from 'antd/es/form';
import {
  CbPromoCodeKind,
  CbDiscountDuration,
  SubProductSubType,
} from 'api/graphql-global-types';
// UI
import {
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  RadioChangeEvent,
  Select,
  Space,
  Switch,
  Checkbox,
  Typography,
} from 'antd';
// Constants
import {
  CB_PROMO_CODE_KIND_OPTIONS,
  CB_PROMO_CODE_KIND_DESCRIPTIONS,
  CB_DISCOUNT_DURATION_OPTIONS,
} from 'helpers/cbPromoCodes/constants';
import { commonTimeFormat } from 'constants/global';
// Helpers
import { formItemRules } from 'helpers/cbPromoCodes/form-item-rules';
// Components
import TargetsSelect from '../TargetsSelect/TargetsSelect';
// Styles
import styles from './CreateCbPromoCodeForm.module.scss';

const { Text } = Typography;
const { TextArea } = Input;

const formItemLayout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 14 },
};

export const cbPromoCodeInitialValues = {
  kind: CbPromoCodeKind.CreditFree,
  code: '',
  startsAt: null,
  expiresAt: null,
  limitTotalUses: false,
  totalUses: null,
  perAccountLimit: 1,
  isActive: true,
  note: '',
  creditAmount: null,
  matchPercent: 100,
  capMaxPromoPerRedemption: false,
  maxPromoPerRedemption: null,
  percentOff: null,
  duration: CbDiscountDuration.once,
  durationMonths: null,
  trialDays: null,
  applicableTargets: [
    { product: SubProductSubType.pro_plan, recurrence: null },
    { product: SubProductSubType.team_plan, recurrence: null },
    { product: SubProductSubType.full_plan, recurrence: null },
  ],
};

export type CreateCbPromoCodeFormProps = {
  formInstance?: FormInstance;
};

const CreateCbPromoCodeForm: React.FC<CreateCbPromoCodeFormProps> = ({
  formInstance,
}) => {
  const [formLocal] = Form.useForm();
  const form = formInstance || formLocal;

  const [kind, setKind] = useState<CbPromoCodeKind>(
    cbPromoCodeInitialValues.kind
  );
  const [duration, setDuration] = useState<CbDiscountDuration>(
    cbPromoCodeInitialValues.duration
  );
  const [limitTotalUses, setLimitTotalUses] = useState<boolean>(false);
  const [capPerRedemption, setCapPerRedemption] = useState<boolean>(false);

  const handleKindChange = (e: RadioChangeEvent) => {
    setKind(e.target.value);
  };

  const handleDurationChange = (value: CbDiscountDuration) => {
    setDuration(value);
  };

  const handleLimitTotalUsesChange = () => {
    setLimitTotalUses((prev) => !prev);
    form.resetFields(['totalUses']);
  };

  const handleCapChange = () => {
    setCapPerRedemption((prev) => !prev);
    form.resetFields(['maxPromoPerRedemption']);
  };

  const isDiscount = kind === CbPromoCodeKind.SubscriptionDiscount;
  const isTrial = kind === CbPromoCodeKind.SubscriptionTrial;

  return (
    <Form
      form={form}
      layout="horizontal"
      name="CreateCbPromoCodeForm"
      initialValues={cbPromoCodeInitialValues}
      autoComplete="off"
      validateTrigger="onSubmit"
      {...formItemLayout}
    >
      <Form.Item
        name="kind"
        label="Type"
        rules={formItemRules.kind}
        className={styles.kindFormItem}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Radio.Group className={styles.kindGroup} onChange={handleKindChange}>
          {CB_PROMO_CODE_KIND_OPTIONS.map(({ label, value }) => (
            <Radio.Button key={value} value={value}>
              <span className={styles.kindLabel}>{label}</span>
              <span className={styles.kindDesc}>
                {CB_PROMO_CODE_KIND_DESCRIPTIONS[value]}
              </span>
            </Radio.Button>
          ))}
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="code"
        label="Vanity code"
        tooltip="Optional. Leave blank to auto-generate an unguessable code. 4–32 characters of letters, numbers, _ or -."
        rules={formItemRules.code}
      >
        <Input placeholder="AUTO-GENERATED" style={{ maxWidth: 260 }} />
      </Form.Item>

      <Form.Item
        name="startsAt"
        label="Starts at"
        tooltip="Optional. Leave blank to activate immediately."
      >
        <DatePicker
          showTime={{ format: 'h:mmA', use12Hours: true }}
          format={commonTimeFormat}
          style={{ maxWidth: 300 }}
        />
      </Form.Item>

      <Form.Item
        name="expiresAt"
        label="Expires at"
        tooltip="Optional. Leave blank for no expiry."
      >
        <DatePicker
          showTime={{ format: 'h:mmA', use12Hours: true }}
          format={commonTimeFormat}
          style={{ maxWidth: 300 }}
        />
      </Form.Item>

      <Form.Item
        label="Total uses"
        tooltip="Global cap across all accounts. Leave unchecked for unlimited."
      >
        <Space size="small" align="start">
          <Form.Item
            name="totalUses"
            noStyle
            rules={limitTotalUses ? formItemRules.totalUses : undefined}
          >
            <InputNumber
              min={1}
              disabled={!limitTotalUses}
              aria-label="Total uses"
            />
          </Form.Item>
          <Form.Item name="limitTotalUses" valuePropName="checked" noStyle>
            <Checkbox onChange={handleLimitTotalUsesChange}>Limit</Checkbox>
          </Form.Item>
        </Space>
      </Form.Item>

      <Form.Item
        name="perAccountLimit"
        label="Per-account limit"
        tooltip="How many times one CB account (team if one exists, else personal) may redeem."
        rules={formItemRules.perAccountLimit}
      >
        <InputNumber min={1} />
      </Form.Item>

      {/* ---- Kind-specific economics (immutable after creation) ---- */}
      {kind === CbPromoCodeKind.CreditFree && (
        <Form.Item
          name="creditAmount"
          label="Credit amount ($)"
          tooltip="Flat wallet credit granted on redemption."
          rules={formItemRules.creditAmount}
        >
          <InputNumber min={0.01} step={0.01} precision={2} />
        </Form.Item>
      )}

      {kind === CbPromoCodeKind.CreditMatch && (
        <>
          <Form.Item
            name="matchPercent"
            label="Match percent (%)"
            tooltip="100 = 1:1 match. May exceed 100."
            rules={formItemRules.matchPercent}
          >
            <InputNumber min={0.01} step={0.01} precision={2} />
          </Form.Item>

          <Form.Item
            label="Per-redemption cap"
            tooltip="Caps the bonus minted in a single top-up. Leave unchecked for uncapped."
          >
            <Space size="small" align="start">
              <Form.Item
                name="maxPromoPerRedemption"
                noStyle
                rules={
                  capPerRedemption
                    ? formItemRules.maxPromoPerRedemption
                    : undefined
                }
              >
                <InputNumber
                  min={0.01}
                  step={0.01}
                  precision={2}
                  disabled={!capPerRedemption}
                  aria-label="Per-redemption cap"
                />
              </Form.Item>
              <Form.Item
                name="capMaxPromoPerRedemption"
                valuePropName="checked"
                noStyle
              >
                <Checkbox onChange={handleCapChange}>Cap</Checkbox>
              </Form.Item>
            </Space>
          </Form.Item>
        </>
      )}

      {isDiscount && (
        <>
          <Form.Item
            name="percentOff"
            label="Discount (%)"
            tooltip="Greater than 0 and at most 100."
            rules={formItemRules.percentOff}
          >
            <InputNumber min={0.01} max={100} step={0.01} precision={2} />
          </Form.Item>

          <Form.Item
            name="duration"
            label="Duration"
            tooltip="'Repeating' counts calendar months, not billing periods — on a yearly price any repeating duration below 12 months is identical to 'once'."
            rules={formItemRules.duration}
          >
            <Select
              options={CB_DISCOUNT_DURATION_OPTIONS}
              onChange={handleDurationChange}
              style={{ maxWidth: 200 }}
            />
          </Form.Item>

          {duration === CbDiscountDuration.repeating && (
            <Form.Item
              name="durationMonths"
              label="Duration (months)"
              tooltip="Calendar months, not billing periods. On a yearly price any value below 12 discounts the whole first annual invoice — identical to 'once'."
              rules={formItemRules.durationMonths}
            >
              <InputNumber min={1} />
            </Form.Item>
          )}
        </>
      )}

      {isTrial && (
        <Form.Item
          name="trialDays"
          label="Trial days"
          tooltip="1–730 (Stripe caps trials at 2 years). Free accounts only; the trial zeroes the first invoice."
          rules={formItemRules.trialDays}
        >
          <InputNumber min={1} max={730} />
        </Form.Item>
      )}

      {(isDiscount || isTrial) && (
        <Form.Item
          name="applicableTargets"
          label="Applies to"
          tooltip="Restrict which plans/addons this code targets. Preselected to the paid plans — remove all targets to apply to everything."
          rules={formItemRules.applicableTargets}
        >
          <TargetsSelect />
        </Form.Item>
      )}

      <Form.Item
        name="isActive"
        label="Active"
        valuePropName="checked"
        tooltip="Paused codes cannot be redeemed. This is the only way to retire a code later — codes are never deleted."
      >
        <Switch />
      </Form.Item>

      <Form.Item
        name="note"
        label="Internal note"
        tooltip="Sales-only, never shown to brands."
        rules={formItemRules.note}
      >
        <TextArea rows={2} maxLength={2000} showCount />
      </Form.Item>

      <Text type="secondary" style={{ display: 'block', marginLeft: 8 }}>
        Economics (type, code, amounts, trial length) are immutable after
        creation — to change them, retire this code and create a new one.
        Discount duration and targets stay editable, for future redemptions
        only.
      </Text>
    </Form>
  );
};

export default CreateCbPromoCodeForm;
